/**
 * How long a battery-swap dock takes to exchange a pack.
 *
 * The one number an org sets (`organizations.settings.dock.swap_duration_s`)
 * that decides the rotation planner's turnaround term, the width of a swap
 * block on the coverage Gantt, how long a dock reporting `swapping` says it
 * needs, and the resupply budget stamped on a self-swap cycle.
 *
 * Mirrors src/lib/dock-swap-duration.ts on the backend - the two are separate
 * TypeScript projects and cannot share a module, so keep them in step. The same
 * arrangement as src/lib/handover-separation.ts and @/lib/handover.
 *
 * Durations are stored and transported in WHOLE SECONDS.
 */

/**
 * Hard envelope, mirroring the Zod bounds in src/lib/dock-swap-duration.ts.
 *
 * The floor is the load-bearing half. Too LONG is conservative: the planner
 * commits the relief earlier than it had to. Too SHORT promises the planner a
 * machine that does not exist, and the station goes uncovered for the
 * difference. Below a minute the number is not a slow-machine allowance, it is
 * a typo. The ceiling is generous because being wrong upward costs only
 * efficiency, and admitting a machine nobody has measured is the point.
 */
export const SWAP_DURATION_BOUNDS_S = [60, 1800] as const;

/**
 * The platform default, and what an org that never opens Settings resolves to.
 *
 * AirScort measured a swap at 4-5 minutes (Itay, relayed 2026-09-02,
 * docs/AIRSCORT-ANSWERS-2026-09-02-SWAP-DOCK.md answer 8). This is the top of
 * that range, deliberately: the conservative end of a vendor range is the end
 * that cannot promise the planner a machine it does not have.
 */
export const DEFAULT_SWAP_DURATION_SECONDS = 5 * 60;

/**
 * Read the org's swap duration out of whatever `organizations.settings` holds.
 *
 * Total, like resolveWindLimit and resolveHandoverSeparationM: the key is absent
 * on every row predating this feature, JSONB guarantees nothing about the shape,
 * and neither the settings page nor the attach wizard's preview may fail to
 * render because a config value is malformed. Out-of-range values clamp rather
 * than fall back, matching the backend resolver exactly.
 */
export function resolveSwapDurationSeconds(settings: unknown): number {
  const raw = settings && typeof settings === 'object'
    ? (settings as Record<string, unknown>).dock
    : undefined;
  if (!raw || typeof raw !== 'object') return DEFAULT_SWAP_DURATION_SECONDS;

  const value = (raw as Record<string, unknown>).swap_duration_s;
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_SWAP_DURATION_SECONDS;

  const [min, max] = SWAP_DURATION_BOUNDS_S;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * "5:00" for 300 - the minutes reading of a duration typed in seconds.
 *
 * The field is in seconds because that is the stored unit and there is then no
 * conversion to argue about anywhere in the stack. This is what stops that being
 * hostile: a swap is a multi-minute event, and an operator judging whether 420
 * is a sane figure for their machine is thinking in minutes.
 */
export function formatSwapDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '--:--';
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}
